/**
 * Inference Shared Internals
 *
 * Config, type checks, blob helpers, metadata parsing, and object creation
 * used by individual inference operation modules.
 */

import type { SceneObjectData } from 'trace-viewer';
import { Utility } from 'trace-viewer';
import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { IMAGE_EXTENSIONS, VIDEO_EXTENSIONS, DECODED_IMAGE_EXTENSIONS } from 'trace-viewer/scene';
import { Video } from 'trace-viewer/behaviors';
import { CreateObjectCommand, getUndoManager, topLevelParent } from 'trace-viewer/editor';

// ── Config ─────────────────────────────────────────────────

export const INFERENCE_API_URL =
    (import.meta.env?.VITE_INFERENCE_API_URL as string | undefined) ??
    (import.meta.env?.VITE_SHARP_API_URL as string | undefined) ??
    'https://inference.marn.dev';

// ── Warmup + Request ───────────────────────────────────────

const WARMUP_POLL_INTERVAL_MS = 2_000;
const WARMUP_MAX_WAIT_MS = 2 * 60_000; // 2 minutes
const HEALTH_PROBE_TIMEOUT_MS = 20_000; // orchestrator may hold the connection open while booting
const REQUEST_TIMEOUT_MS = 10 * 60_000; // 10 minutes — absolute ceiling

/** Callback for reporting inference progress phases to the UI. */
export type InferenceStatusCallback = (status: string) => void;

export interface InferencePostOptions {
    /** Override the health URL. Default: `${INFERENCE_API_URL}/${service}/health`. */
    healthUrl?: string;
    /** Predicate on health JSON to decide readiness. Default: `body => !!body.model_loaded` */
    healthReady?: (body: Record<string, unknown>) => boolean;
    /** Max time to wait for warmup before failing. Default: 2 minutes. */
    warmupTimeoutMs?: number;
    /** Max time for the main request before aborting. Default: 10 minutes. */
    requestTimeoutMs?: number;
}

async function fetchWithTimeout(url: string, init: RequestInit, timeoutMs: number): Promise<Response> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    // Combine our timeout signal with any signal the caller already put in
    // `init` so caller-driven cancellation isn't silently swallowed.
    const signals: AbortSignal[] = [controller.signal];
    if (init.signal) signals.push(init.signal);
    const signal = signals.length === 1 ? signals[0] : AbortSignal.any(signals);
    try {
        return await fetch(url, { ...init, signal });
    } finally {
        clearTimeout(timer);
    }
}

/**
 * Probe the health endpoint once. Returns true if the service is ready.
 * Throws if the endpoint doesn't exist (404) — that's a config bug, not a cold start.
 */
async function probeHealth(
    healthUrl: string,
    isReady: (body: Record<string, unknown>) => boolean,
): Promise<boolean> {
    try {
        const resp = await fetchWithTimeout(healthUrl, {}, HEALTH_PROBE_TIMEOUT_MS);
        if (resp.status === 404) {
            throw new Error(`Health endpoint not found: ${healthUrl}`);
        }
        if (!resp.ok) return false;
        const body = await resp.json() as Record<string, unknown>;
        return isReady(body);
    } catch (err) {
        if (err instanceof Error && err.message.startsWith('Health endpoint not found')) {
            throw err;
        }
        return false;
    }
}

/**
 * Poll a health endpoint until the service reports ready, or throw on timeout.
 */
async function waitForWarmup(
    service: string,
    healthUrl: string,
    isReady: (body: Record<string, unknown>) => boolean,
    timeoutMs: number,
    onStatus?: InferenceStatusCallback,
): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    let lastReport = 0;
    while (Date.now() < deadline) {
        if (await probeHealth(healthUrl, isReady)) return;
        // Report elapsed time every 5s so the user sees progress
        const elapsed = Math.floor((timeoutMs - (deadline - Date.now())) / 1000);
        if (elapsed - lastReport >= 5) {
            onStatus?.(`waiting for ${service} to start (${elapsed}s)…`);
            lastReport = elapsed;
        }
        await new Promise(r => setTimeout(r, WARMUP_POLL_INTERVAL_MS));
    }
    throw new Error(`${service} did not become ready within ${Math.round(timeoutMs / 1000)}s.`);
}

/**
 * POST to an inference endpoint, warming up the service first if needed.
 *
 * Always probes `${INFERENCE_API_URL}/${service}/health` before the main POST.
 * If the service is cold, polls the health endpoint (showing progress via
 * `onStatus`) until ready or the warmup ceiling is hit. The main POST is
 * bounded by a request timeout so it can never hang forever.
 *
 * @param service - Service id used to derive the health URL and status messages.
 * @param buildRequest - Returns `[url, init]` for the POST. Called once, after warmup.
 * @param opts - Optional overrides for health URL, readiness predicate, timeouts.
 * @param onStatus - Optional callback for UI status updates.
 */
export async function inferencePost(
    service: string,
    buildRequest: () => [string, RequestInit],
    opts: InferencePostOptions = {},
    onStatus?: InferenceStatusCallback,
): Promise<Response> {
    const healthUrl = opts.healthUrl ?? `${INFERENCE_API_URL}/${service}/health`;
    const isReady = opts.healthReady ?? ((body: Record<string, unknown>) => !!body.model_loaded);
    const warmupTimeoutMs = opts.warmupTimeoutMs ?? WARMUP_MAX_WAIT_MS;
    const requestTimeoutMs = opts.requestTimeoutMs ?? REQUEST_TIMEOUT_MS;

    // Preflight: probe health. If cold, wait for warmup with progress updates.
    const ready = await probeHealth(healthUrl, isReady);
    if (!ready) {
        console.log(`[inference] ${service} not ready, warming up…`);
        onStatus?.(`waiting for ${service} to start…`);
        await waitForWarmup(service, healthUrl, isReady, warmupTimeoutMs, onStatus);
    }

    onStatus?.('generating…');
    let response = await runRequest();

    // If the service died mid-generation and restarted, fetch returns 503.
    // Wait for the next ready signal and try once more — covers the common
    // "container OOM-killed and respawned" pattern. Single retry only;
    // anything beyond that and the operator should investigate.
    if (response.status === 503) {
        console.log(`[inference] ${service} returned 503 mid-request, waiting for restart…`);
        onStatus?.(`${service} restarting, retrying…`);
        await waitForWarmup(service, healthUrl, isReady, warmupTimeoutMs, onStatus);
        onStatus?.('generating…');
        response = await runRequest();
    }

    return response;

    async function runRequest(): Promise<Response> {
        const [url, init] = buildRequest();
        try {
            return await fetchWithTimeout(url, init, requestTimeoutMs);
        } catch (err) {
            if (err instanceof DOMException && err.name === 'AbortError') {
                throw new Error(`${service} request timed out after ${Math.round(requestTimeoutMs / 1000)}s.`);
            }
            throw err;
        }
    }
}

// ── Type Checks ────────────────────────────────────────────

// IMAGE_EXTENSIONS and VIDEO_EXTENSIONS imported from trace-viewer/scene

/** Returns true if the SceneObject has an image source (jpg/jpeg/png). */
export function isImageSceneObject(so: SceneObject): boolean {
    const src = so.src;
    if (!src) return false;
    const ext = src.split('.').pop()?.toLowerCase() ?? '';
    return IMAGE_EXTENSIONS.has(ext);
}

/** Returns true if the SceneObject has a video source (mp4/webm). */
export function isVideoSceneObject(so: SceneObject): boolean {
    const src = so.src;
    if (!src) return false;
    const ext = src.split('.').pop()?.toLowerCase() ?? '';
    return VIDEO_EXTENSIONS.has(ext);
}

/** Returns true if the SceneObject has a GeoTIFF source (.tif/.tiff). */
export function isGeoTiffSceneObject(so: SceneObject): boolean {
    const src = so.src;
    if (!src) return false;
    const ext = src.split('.').pop()?.toLowerCase() ?? '';
    return DECODED_IMAGE_EXTENSIONS.has(ext);
}

// ── Per-frame Metadata ─────────────────────────────────────

/**
 * Per-frame metadata from an inference pipeline (e.g. MapAnything).
 * Sandbox-only — not part of the trace-viewer scene data model.
 */
export interface PageFrameMetadata {
    /** Frame index in the source video */
    frameIndex?: number;
    /** Timestamp in seconds into the source video */
    timestamp?: number;
    /** Number of valid 3D points for this frame */
    numPoints?: number;
    /** 4x4 camera extrinsic matrix (row-major, 16 elements) */
    cameraPose?: number[];
    /** 3x3 camera intrinsic matrix (row-major, 9 elements: [fx,0,cx, 0,fy,cy, 0,0,1]) */
    cameraIntrinsics?: number[];
}

/** Parse X-Frame-Metadata response header into PageFrameMetadata[]. */
export function parseFrameMetadata(response: Response): PageFrameMetadata[] {
    const raw = response.headers.get('X-Frame-Metadata');
    if (!raw) {
        console.warn('[Inference] X-Frame-Metadata header not accessible (server needs Access-Control-Expose-Headers)');
        return [];
    }
    try {
        const parsed = JSON.parse(raw) as Array<Record<string, unknown>>;
        return parsed.map(entry => ({
            frameIndex: typeof entry.frame_index === 'number' ? entry.frame_index : undefined,
            timestamp: typeof entry.timestamp_s === 'number' ? entry.timestamp_s : undefined,
            numPoints: typeof entry.num_points === 'number' ? entry.num_points : undefined,
            cameraPose: Array.isArray(entry.camera_pose)
                ? (Array.isArray(entry.camera_pose[0])
                    ? (entry.camera_pose as number[][]).flat()
                    : entry.camera_pose as number[])
                : undefined,
            cameraIntrinsics: Array.isArray(entry.intrinsics)
                ? (Array.isArray(entry.intrinsics[0])
                    ? (entry.intrinsics as number[][]).flat()
                    : entry.intrinsics as number[])
                : undefined,
        }));
    } catch {
        console.warn('[Inference] Failed to parse X-Frame-Metadata header');
        return [];
    }
}

/** Compute average inter-frame duration from timestamps. Returns 0 if < 2 timestamps. */
export function computeAverageFrameDuration(frames: PageFrameMetadata[]): number {
    const timestamps = frames
        .map(f => f.timestamp)
        .filter((t): t is number => t !== undefined)
        .sort((a, b) => a - b);
    if (timestamps.length < 2) return 0;
    return (timestamps[timestamps.length - 1] - timestamps[0]) / (timestamps.length - 1);
}

// ── Blob Helpers ───────────────────────────────────────────

/**
 * Map an image Blob's MIME type to a file extension the inference servers
 * will accept. Falls back to 'jpg' for unknown types — servers typically
 * sniff content anyway, but the filename should not lie about the format
 * (old behaviour of `sourceObject.src.split('.').pop()` mislabelled .webp
 * and videos as .mp4).
 */
export function imageBlobExtension(blob: Blob): string {
    switch (blob.type) {
        case 'image/png': return 'png';
        case 'image/webp': return 'webp';
        case 'image/gif': return 'gif';
        default: return 'jpg';
    }
}

/**
 * Get an image blob from a SceneObject. Returns the image asset for Image
 * objects, or a PNG of the currently displayed frame for Video objects —
 * so image-consuming inference plugins work against either source.
 */
export async function getImageBlob(sourceObject: SceneObject, traceScene: TraceScene): Promise<Blob> {
    if (isVideoSceneObject(sourceObject)) {
        const video = sourceObject.getBehavior(Video);
        if (!video) {
            throw new Error(
                `Object "${sourceObject.name}" has a video source but no Video behavior — the scene may be partially loaded or corrupted.`,
            );
        }
        const frame = await video.captureCurrentFrame();
        if (!frame) {
            throw new Error(
                `Could not capture a frame from video "${sourceObject.name}". The video may not have loaded yet, or the source is cross-origin without CORS.`,
            );
        }
        return frame;
    }

    const src = sourceObject.src;
    if (!src) throw new Error('Image has no source');

    const normalizedSrc = src.replace(/^\.\//, '');
    const blob = traceScene.resources.getAsset(normalizedSrc) ?? traceScene.resources.getAsset(src);
    if (blob) return blob;

    const resp = await fetch(src);
    if (!resp.ok) throw new Error(`Could not load image "${src}": HTTP ${resp.status}`);
    return resp.blob();
}

/** Get a GeoTIFF blob from a SceneObject's src (resources or remote fetch). */
export async function getGeoTiffBlob(sourceObject: SceneObject, traceScene: TraceScene): Promise<Blob> {
    const src = sourceObject.src;
    if (!src) throw new Error('GeoTIFF has no source');

    const normalizedSrc = src.replace(/^\.\//, '');
    const blob = traceScene.resources.getAsset(normalizedSrc) ?? traceScene.resources.getAsset(src);
    if (blob) return blob;

    const resp = await fetch(src);
    if (!resp.ok) throw new Error(`Could not load GeoTIFF "${src}": HTTP ${resp.status}`);
    return resp.blob();
}

/** Get a video blob from a SceneObject's src (resources or remote fetch). */
export async function getVideoBlob(sourceObject: SceneObject, traceScene: TraceScene): Promise<Blob> {
    const src = sourceObject.src;
    if (!src) throw new Error('Video has no source');

    const normalizedSrc = src.replace(/^\.\//, '');
    const blob = traceScene.resources.getAsset(normalizedSrc) ?? traceScene.resources.getAsset(src);
    if (blob) return blob;

    const resp = await fetch(src);
    if (!resp.ok) throw new Error(`Could not load video "${src}": HTTP ${resp.status}`);
    return resp.blob();
}

// ── Create Result Object ───────────────────────────────────

/**
 * Depth service and MapAnything PLY output Y-down / Z-forward;
 * flip Y and Z to match our Y-up convention. GLB already uses Y-up.
 */
export const POINTCLOUD_SCALE_CORRECTION: [number, number, number] = [1, -1, -1];

/**
 * Compose an authored scale with a per-axis correction.
 */
function applyScaleCorrection(
    scale: [number, number, number] | undefined,
    correction: [number, number, number],
): [number, number, number] {
    const base = scale ?? [1, 1, 1];
    return [base[0] * correction[0], base[1] * correction[1], base[2] * correction[2]];
}

/**
 * Create a new SceneObject from inference output at the source object's pose.
 * @param scaleCorrection Optional per-axis scale multiplier to fix coordinate
 *   system differences (e.g. `[1, -1, -1]` for Y-up <-> Y-down flip).
 */
export async function createObjectFromInference(
    traceScene: TraceScene,
    sourceObject: SceneObject,
    assetPath: string,
    suffix: string,
    scaleCorrection?: [number, number, number],
    behaviors: SceneObjectData['behaviors'] = [],
): Promise<SceneObject> {
    sourceObject.captureTransform();
    const sourceData = sourceObject.getSceneObjectData();
    const parent = sourceObject.getParent();

    const siblingNames = new Set(sourceObject.getSiblings().map(s => s.name));
    const newName = Utility.generateUniqueName(
        `${sourceObject.name}_${suffix}`,
        siblingNames,
    );

    // Compose inherited scale with optional correction.
    // Source scale elements may be `$ref` strings — only multiply numeric ones.
    let finalScale = sourceData.transform?.scale;
    if (scaleCorrection) {
        finalScale = applyScaleCorrection(finalScale, scaleCorrection);
    }

    const objectData: SceneObjectData = {
        name: newName,
        src: `./${assetPath}`,
        transform: {
            ...sourceData.transform,
            scale: finalScale,
            parent: parent?.name,
        },
        behaviors,
    };

    // No parent given means the top of the scene: the DOCUMENT ROOT (D8).
    const targetParent = parent ?? topLevelParent(traceScene);
    const newObject = await traceScene.createSceneObjectFromData(
        objectData,
        targetParent ?? undefined,
    );

    if (!getUndoManager().isUndoRedoing) {
        getUndoManager().push(
            new CreateObjectCommand(newObject, targetParent, objectData),
        );
    }

    traceScene.select(newObject);
    return newObject;
}
